import React, {useCallback, useMemo, useState} from 'react';

import {
  type CollectionOverviewAnalyticsContextValue,
  trackCollectionOverviewInteraction,
} from '../ArtifactOverview/collectionOverviewAnalytics';
import {
  type ArtifactVersionMetricGridPanel,
  type ArtifactVersionMetricGridPanelConfig,
} from './artifactVersionsGridTypes';

export type ArtifactVersionMetricPanelEditorSession = {
  gridId: string;
  initialChangedConfigKeys?: string[];
  panel: ArtifactVersionMetricGridPanel;
  membershipIds: string[];
  updateConfig: (config: Partial<ArtifactVersionMetricGridPanelConfig>) => void;
};

type PanelConfigTrackingSession = {
  baselineConfig: ArtifactVersionMetricGridPanelConfig;
  changedConfigKeys: Set<string>;
  latestConfig: ArtifactVersionMetricGridPanelConfig;
  session: ArtifactVersionMetricPanelEditorSession;
};

type ArtifactVersionMetricsContextValue = {
  analyticsContext?: CollectionOverviewAnalyticsContextValue;
  artifactCollectionId?: string;
  closePanelEditor: () => void;
  panelEditor: ArtifactVersionMetricPanelEditorSession | null;
  syncPanelEditor: (session: ArtifactVersionMetricPanelEditorSession) => void;
  togglePanelEditor: (session: ArtifactVersionMetricPanelEditorSession) => void;
  isEditing: boolean;
};

const ArtifactVersionMetricsContext =
  React.createContext<ArtifactVersionMetricsContextValue>({
    closePanelEditor: () => {},
    panelEditor: null,
    syncPanelEditor: () => {},
    togglePanelEditor: () => {},
    isEditing: false,
  });

type ArtifactVersionMetricsContextProviderProps = {
  analyticsContext?: CollectionOverviewAnalyticsContextValue;
  artifactCollectionId?: string;
  children: React.ReactNode;
  isEditing: boolean;
};

export const ArtifactVersionMetricsContextProvider = ({
  analyticsContext,
  artifactCollectionId,
  children,
  isEditing,
}: ArtifactVersionMetricsContextProviderProps) => {
  const [panelEditor, setPanelEditor] =
    useState<ArtifactVersionMetricPanelEditorSession | null>(null);
  const configTrackingRef = React.useRef<PanelConfigTrackingSession | null>(
    null
  );

  const flushConfigTracking = useCallback(
    (closeReason: 'drawer_closed' | 'edit_mode_exited' | 'panel_changed') => {
      const tracking = configTrackingRef.current;
      if (tracking == null || tracking.changedConfigKeys.size === 0) {
        configTrackingRef.current = null;
        return;
      }

      const {session, changedConfigKeys} = tracking;
      trackCollectionOverviewInteraction(analyticsContext, 'configure_panel', {
        chartType: session.panel.config.chartType,
        closeReason,
        configKeys: Array.from(changedConfigKeys).sort(),
        gridId: session.gridId,
        panelId: session.panel.__id__,
        selectedVersionCount: session.membershipIds.length,
      });
      configTrackingRef.current = null;
    },
    [analyticsContext]
  );

  const buildTrackedSession = useCallback(
    (
      session: ArtifactVersionMetricPanelEditorSession
    ): ArtifactVersionMetricPanelEditorSession => {
      const baselineConfig = session.panel.config;
      const changedConfigKeys = new Set(session.initialChangedConfigKeys ?? []);
      configTrackingRef.current = {
        baselineConfig,
        changedConfigKeys,
        latestConfig: session.panel.config,
        session,
      };

      return {
        ...session,
        updateConfig: config => {
          const tracking = configTrackingRef.current;
          if (tracking != null) {
            tracking.latestConfig = {...tracking.latestConfig, ...config};
          }
          Object.entries(config).forEach(([key, value]) => {
            if (
              !Object.is(
                value,
                baselineConfig[
                  key as keyof ArtifactVersionMetricGridPanelConfig
                ]
              )
            ) {
              changedConfigKeys.add(key);
            } else {
              changedConfigKeys.delete(key);
            }
          });
          session.updateConfig(config);
        },
      };
    },
    []
  );

  const hasSamePanelEditorSession = (
    current: ArtifactVersionMetricPanelEditorSession | null,
    next: ArtifactVersionMetricPanelEditorSession
  ) =>
    current?.gridId === next.gridId &&
    current.panel.__id__ === next.panel.__id__;

  // Drop any open editor session when edit mode exits (publish/discard/close)
  // so re-entering edit mode doesn't reopen the previously-open drawer. Handled
  // as a render-time reset rather than an effect (preferred per repo guidance).
  const previousIsEditingRef = React.useRef(isEditing);
  // oxlint-disable-next-line react/refs
  if (previousIsEditingRef.current !== isEditing) {
    // oxlint-disable-next-line react/refs
    previousIsEditingRef.current = isEditing;
    if (!isEditing && panelEditor != null) {
      // oxlint-disable-next-line react/refs
      flushConfigTracking('edit_mode_exited');
      setPanelEditor(null);
    }
  }

  const closePanelEditor = useCallback(() => {
    flushConfigTracking('drawer_closed');
    setPanelEditor(null);
  }, [flushConfigTracking]);

  const togglePanelEditor = useCallback(
    (session: ArtifactVersionMetricPanelEditorSession) => {
      setPanelEditor(current => {
        if (hasSamePanelEditorSession(current, session)) {
          flushConfigTracking('drawer_closed');
          return null;
        }

        flushConfigTracking('panel_changed');
        return buildTrackedSession(session);
      });
    },
    [buildTrackedSession, flushConfigTracking]
  );

  const syncPanelEditor = useCallback(
    (session: ArtifactVersionMetricPanelEditorSession) => {
      setPanelEditor(current => {
        if (current == null || !hasSamePanelEditorSession(current, session)) {
          flushConfigTracking('panel_changed');
          return buildTrackedSession(session);
        }

        configTrackingRef.current = configTrackingRef.current
          ? {
              ...configTrackingRef.current,
              latestConfig: session.panel.config,
              session,
            }
          : {
              baselineConfig: session.panel.config,
              changedConfigKeys: new Set(
                session.initialChangedConfigKeys ?? []
              ),
              latestConfig: session.panel.config,
              session,
            };
        return {
          ...session,
          updateConfig: current.updateConfig,
        };
      });
    },
    [buildTrackedSession, flushConfigTracking]
  );

  const value = useMemo(
    () => ({
      analyticsContext,
      artifactCollectionId,
      closePanelEditor,
      panelEditor,
      syncPanelEditor,
      togglePanelEditor,
      isEditing,
    }),
    [
      analyticsContext,
      artifactCollectionId,
      closePanelEditor,
      panelEditor,
      syncPanelEditor,
      togglePanelEditor,
      isEditing,
    ]
  );

  return (
    <ArtifactVersionMetricsContext.Provider value={value}>
      {children}
    </ArtifactVersionMetricsContext.Provider>
  );
};

export function useArtifactVersionMetricsContext() {
  return React.useContext(ArtifactVersionMetricsContext);
}
